import './dropDowns.css';

import { useEffect, useRef } from 'react';
import { useRecoilValue } from 'recoil';
import { userConfigState } from '../../../recoil/atoms/userConfigState';

import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxOutlinedIcon from '@mui/icons-material/CheckBoxOutlined';

import {
  getCommonScoresForAllReportVersions,
  getFlattenedReportVersionScoreKeys,
  getLatestReportVersions,
  getReportScoreDescription,
  getReportScoreName,
  getReportVersionDisplayName,
  getScoreTitleFontSize,
} from '../../documents/report/reportVersions';

import { translate } from '../../../../common/language/translations';
import { ReportRatingRequirements } from '../../../types/navigation';
import { deepCloneObject } from '../../../utils/commonUtils';
import { StarRating } from '../input/StarRating';
import { useOpenGlobalModal } from '../../../recoil/hooks/useOpenGlobalModal';


interface ReportRatingDropDownProps {
  id: string;

  selectedOptions: ReportRatingRequirements[];
  setSelectedOptions: (ratingRequirements: ReportRatingRequirements[]) => void;

  isDropDownExpanded: boolean;
  setIsDropDownExpanded: (value: boolean) => void;

  defaultDropDownText: string; // static language key
  defaultDropDownTextColor: string;
  maxHeight?: string; // controls scrolling
}

export const ReportRatingDropDown: React.FC<ReportRatingDropDownProps> = ({
  id,

  selectedOptions,
  setSelectedOptions,

  isDropDownExpanded,
  setIsDropDownExpanded,

  defaultDropDownText,
  defaultDropDownTextColor,
  maxHeight,
}) => {

  const userConfig = useRecoilValue(userConfigState);
  const { openTextModal } = useOpenGlobalModal();

  const dropDownRef = useRef<HTMLDivElement>(null);

  const latestReportVersions = getLatestReportVersions(userConfig?.club);
  const commonScoreKeys = getCommonScoresForAllReportVersions(userConfig?.club); // overallScore is currently mandatory for all versions


  const handleDropDownClick = () => {
    if (isDropDownExpanded) {
      removeDropDownExpansion();
    }

    else {
      expandDropDown();
    }
  };


  const expandDropDown = () => {
    const element = document.getElementById(id);
    if (element) {
      element.style.transition = 'height 150ms';
      element.style.height = 'auto';
      element.style.zIndex = '100';
    }
    setIsDropDownExpanded(true);
  };


  const removeDropDownExpansion = () => {
    const element = document.getElementById(id);
    if (element) {
      element.style.transition = '75ms';
      element.style.height = '28px';
      element.style.zIndex = '1';
    }
    setIsDropDownExpanded(false);
    if (dropDownRef.current) {
      dropDownRef.current.scrollTop = 0;
    }
  };


  const showSelectedOptions = () => {
    let displayText = '';

    // we first want to show the common scores, if any
    const commonVersionEntry = selectedOptions.find(option => option.reportVersion === undefined);
    if (commonVersionEntry) {
      if (latestReportVersions.length > 1) {
        displayText += translate('allVersions', userConfig?.language) + ': ';
      }

      commonScoreKeys.forEach(scoreKey => {
        const scoreValue = commonVersionEntry.reportScores?.find(score => score.scoreKey === scoreKey)?.scoreValue;
        if (scoreValue) {
          displayText += getReportScoreName(scoreKey, userConfig?.language, userConfig?.club) + ' (' + scoreValue + '), ';
        }
      });
    }

    if (displayText.endsWith(', ')) {
      displayText = displayText.slice(0, -2);
    }

    // then we want to show version specific scores, if any
    latestReportVersions.forEach(version => {
      const versionEntry = selectedOptions.find(option => option.reportVersion === version);
      if (versionEntry) {
        if (displayText.length > 0) displayText += ' | ';
        displayText += getReportVersionDisplayName(version, userConfig?.language, userConfig?.club);

        if (versionEntry.reportScores) {
          displayText += ': ';
        }

        versionEntry.reportScores?.forEach(score => {
          displayText += getReportScoreName(score.scoreKey, userConfig?.language, userConfig?.club) + ' (' + score.scoreValue + '), ';
        });
      }
      if (displayText.endsWith(', ')) {
        displayText = displayText.slice(0, -2);
      }
    });

    return displayText;
  };


  const handleCheckboxClick = (version: string, versionIsSelected: boolean) => {

    // if version is selected and any ratings from this version are selected, do nothing
    if (versionIsSelected && selectedOptions.some(option => option.reportVersion === version && option.reportScores)) {
      return;
    }

    if (versionIsSelected) {
      setSelectedOptions(selectedOptions.filter(option => option.reportVersion !== version));
    }
    else {
      setSelectedOptions([...selectedOptions, { reportVersion: version }]);
    }
  };


  const handleScoreChange = (value: number | null, scoreKey: string, version?: string) => {
    let newOptions = deepCloneObject(selectedOptions) as ReportRatingRequirements[];
    const versionEntry = newOptions.find(option => option.reportVersion === version);

    if (versionEntry) {
      let currentReportScores = versionEntry.reportScores ? [...versionEntry.reportScores] : [];
      const currentScore = currentReportScores.find(score => score.scoreKey === scoreKey);

      if (value === null && currentScore) {
        currentReportScores = currentReportScores.filter(score => score.scoreKey !== scoreKey);
      }
      else if (value !== null) {
        if (currentScore) {
          currentReportScores = currentReportScores.filter(score => score.scoreKey !== scoreKey);
        }
        currentReportScores.push({ scoreKey: scoreKey, scoreValue: value });
      }

      if (currentReportScores.length > 0) {
        versionEntry.reportScores = currentReportScores;
      }
      else {
        newOptions = newOptions.filter(option => option.reportVersion !== version);
      }
    }

    else if (value !== null) {
      newOptions.push({ reportVersion: version, reportScores: [{ scoreKey: scoreKey, scoreValue: value }] });
    }

    setSelectedOptions(newOptions);
  };


  useEffect(() => {
    if (!isDropDownExpanded) {
      removeDropDownExpansion();
    }
  }, [isDropDownExpanded]); // eslint-disable-line react-hooks/exhaustive-deps


  return (
    <div
      className='drop-down-container'
      id={id}
      style={{ maxHeight: maxHeight }}
    >

      <div className='drop-down-button' onClick={() => handleDropDownClick()}>

        {selectedOptions.length > 0 && <div className='drop-down-button-overlay' />}

        <div className='drop-down-button-text'>
          {selectedOptions.length === 0 ? (
            <div style={{ color: defaultDropDownTextColor }}>
              {translate(defaultDropDownText, userConfig?.language)}
            </div>
          ) : (
            <div style={{ color: '#000000' }}>
              {showSelectedOptions()}
            </div>
          )}
        </div>

        {!isDropDownExpanded && (
          <div className='drop-down-icon'>
            <ArrowDropDownIcon style={{ fontSize: 24 }} />
          </div>
        )}

        {isDropDownExpanded && (
          <div className='drop-down-icon'>
            <ArrowDropUpIcon style={{ fontSize: 24 }} />
          </div>
        )}
      </div>

      <div className='drop-down-space'>&nbsp;</div>

      <div
        ref={dropDownRef}
        className={'drop-down-options' + (maxHeight ? ' drop-down-options-scroll' : '')}
        style={{ maxHeight: maxHeight, paddingBottom: 15 }}
      >

        {latestReportVersions.length > 1 && (
          <div>
            <div className='rating-drop-down-divider' style={{ marginTop: 10 }} />

            <div className='metric-drop-down-group-title-container' style={{ marginTop: -9, justifyContent: 'center' }}>
              <div
                className='flex-row align-center rating-drop-down-group-title'
                style={{ fontSize: 13, color: '#000000', padding: '0px 10px' }}
              >
                {translate('allVersions', userConfig?.language)}
              </div>
            </div>
          </div>
        )}

        <div className='report-rating-drop-down-scores-container'>
          {commonScoreKeys.map(scoreKey => {
            if (!userConfig) return null;

            let scoreValue = 0;
            const reportVersionEntry = selectedOptions.find(option => option.reportVersion === undefined);
            if (reportVersionEntry) {
              const scoreValueEntry = reportVersionEntry.reportScores?.find(score => score.scoreKey === scoreKey);
              if (scoreValueEntry) {
                scoreValue = scoreValueEntry.scoreValue;
              }
            }

            const scoreName = getReportScoreName(scoreKey, userConfig?.language, userConfig?.club) ?? scoreKey;
            const scoreDescription = getReportScoreDescription(scoreKey, userConfig?.language, userConfig?.club);

            return (
              <div
                className='report-rating-drop-down-score-container'
                style={{ marginTop: 8 }}
                key={scoreKey}
              >
                <div
                  className={'report-rating-drop-down-score-title' + (scoreDescription ? ' report-rating-title-clickable' : '')}
                  style={{ fontSize: getScoreTitleFontSize(scoreName, 13, 11) }}
                  onClick={() => scoreDescription ? openTextModal(scoreName, scoreDescription) : undefined}
                  title={scoreDescription ? translate('showDescription', userConfig?.language) : undefined}
                >
                  {scoreName}
                </div>

                <StarRating
                  value={scoreValue}
                  onChange={value => handleScoreChange(value, scoreKey)}
                  size={20}
                  borderColor='#515254'
                  color='#171c25'
                />
              </div>
            );
          })}
        </div>

        {latestReportVersions.length > 1 && latestReportVersions.map((version, index) => {

          const versionIsSelected = selectedOptions.some(option => option.reportVersion === version);
          const scoreKeys = getFlattenedReportVersionScoreKeys(version);

          return (
            <div key={index}>
              <div className='rating-drop-down-divider'/>

              <div className='metric-drop-down-group-title-container' style={{ marginTop: -9, justifyContent: 'center' }}>
                <div className='flex-row align-center rating-drop-down-group-title' style={{ fontSize: 13, color: '#000000' }}>
                  {getReportVersionDisplayName(version, userConfig?.language, userConfig?.club)}

                  <div
                    className='report-rating-drop-down-checkbox-container'
                    onClick={() => handleCheckboxClick(version, versionIsSelected)}
                  >
                    {versionIsSelected && <CheckBoxOutlinedIcon style={{ fontSize: 21, color: '#171c25' }} />}
                    {!versionIsSelected && <CheckBoxOutlineBlankIcon style={{ fontSize: 19, color: '#515254' }} />}
                  </div>
                </div>
              </div>

              <div className='report-rating-drop-down-scores-container'>
                {scoreKeys.map(scoreKey => {
                  if (!userConfig || commonScoreKeys.includes(scoreKey)) return null;

                  let scoreValue = 0;
                  const reportVersionEntry = selectedOptions.find(option => option.reportVersion === version);
                  if (reportVersionEntry) {
                    const scoreValueEntry = reportVersionEntry.reportScores?.find(score => score.scoreKey === scoreKey);
                    if (scoreValueEntry) {
                      scoreValue = scoreValueEntry.scoreValue;
                    }
                  }

                  const scoreName = getReportScoreName(scoreKey, userConfig?.language, userConfig?.club) ?? scoreKey;
                  const scoreDescription = getReportScoreDescription(scoreKey, userConfig?.language, userConfig?.club);

                  return (
                    <div
                      className='report-rating-drop-down-score-container'
                      style={{ marginTop: 8 }}
                      key={scoreKey}
                    >
                      <div
                        className={'report-rating-drop-down-score-title' + (scoreDescription ? ' report-rating-title-clickable' : '')}
                        style={{ fontSize: getScoreTitleFontSize(scoreName, 13, 11) }}
                        onClick={() => scoreDescription ? openTextModal(scoreName, scoreDescription) : undefined}
                        title={scoreDescription ? translate('showDescription', userConfig?.language) : undefined}
                      >
                        {scoreName}
                      </div>

                      <StarRating
                        value={scoreValue}
                        onChange={value => handleScoreChange(value, scoreKey, version)}
                        size={20}
                        borderColor='#515254'
                        color='#171c25'
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
